import { Languages } from 'lucide-react';
import { i18n, getEnabledLanguages, LANG_STORAGE_KEY } from '@/shared/i18n';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import type { LanguageCode } from '@/shared/i18n';

/**
 * LanguageSwitcher — shown ONLY when more than one language is enabled.
 *
 * Reads the language registry to determine which languages are available.
 * Persists the user's choice via i18next.changeLanguage + localStorage.
 * Returns null when only one language is enabled — no switcher rendered.
 *
 * FSD boundary: widgets may import from shared only (not app). This component
 * drives language changes directly through the shared i18n instance rather
 * than the app-layer useI18n hook, which keeps the boundary clean.
 * The I18nextProvider in the app layer ensures all react-i18next hooks
 * (including useTranslation used here) share the same i18n instance.
 */

export function LanguageSwitcher() {
  const enabledLanguages = getEnabledLanguages();

  // Conditional: do not render when only one (or zero) languages are enabled
  if (enabledLanguages.length <= 1) {
    return null;
  }

  return <LanguageSwitcherInner />;
}

function LanguageSwitcherInner() {
  // useTranslation subscribes to language changes and re-renders on switch
  const { i18n: i18nInstance } = useTranslation();
  const enabledLanguages = getEnabledLanguages();
  const currentLanguage = i18nInstance.language as LanguageCode;

  function handleSelect(code: LanguageCode) {
    void i18n.changeLanguage(code);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch {
      // localStorage may be unavailable in some environments
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Switch language"
          data-testid="language-switcher-trigger"
        >
          <Languages className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {enabledLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            aria-current={currentLanguage === lang.code ? 'true' : undefined}
            data-testid={`language-option-${lang.code}`}
          >
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
